import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Api from "../../../services/Api";
import { queryKeys } from "../../../lib/query";
import { SYSTEM_INFO_URL } from "../constants";
import { isEnphaseUser } from "../../../lib/utils/utility";

// ── Country centroids — fallback when API has no lat/lng ──
const COUNTRY_CENTROIDS = {
  US: [39.8, -98.5],
  CA: [56.1, -106.3],
  MX: [23.6, -102.5],
  BR: [-14.2, -51.9],
  AR: [-38.4, -63.6],
  CL: [-35.7, -71.5],
  CO: [4.6, -74.3],
  GB: [55.4, -3.4],
  FR: [46.6, 2.2],
  DE: [51.2, 10.4],
  ES: [40.5, -3.7],
  IT: [41.9, 12.6],
  NL: [52.1, 5.3],
  BE: [50.5, 4.5],
  CH: [46.8, 8.2],
  AT: [47.5, 14.6],
  SE: [60.1, 18.6],
  NO: [60.5, 8.5],
  DK: [56.3, 9.5],
  FI: [61.9, 25.7],
  PL: [51.9, 19.1],
  PT: [39.4, -8.2],
  IE: [53.1, -8.2],
  CZ: [49.8, 15.5],
  RO: [45.9, 24.7],
  HU: [47.2, 19.5],
  GR: [39.1, 21.8],
  UA: [48.4, 31.2],
  RU: [61.5, 105.3],
  TR: [38.9, 35.2],
  IN: [20.6, 78.9],
  CN: [35.9, 104.2],
  JP: [36.2, 138.3],
  KR: [35.9, 127.8],
  SG: [1.4, 103.8],
  TH: [15.9, 100.5],
  MY: [4.2, 101.7],
  ID: [-0.8, 113.9],
  PH: [12.9, 121.8],
  VN: [14.1, 108.3],
  TW: [23.7, 121.0],
  HK: [22.3, 114.2],
  AU: [-25.3, 133.8],
  NZ: [-40.9, 174.9],
  ZA: [-30.6, 22.9],
  EG: [26.8, 30.8],
  NG: [9.1, 8.7],
  KE: [0.0, 37.9],
  AE: [23.4, 53.8],
  SA: [23.9, 45.1],
  IL: [31.0, 34.9],
  QA: [25.3, 51.2],
};

/**
 * Try multiple paths to extract lat/lng from the systeminfo response.
 * address.location may be an empty array [] or an object — handle both.
 * Returns { lat, lng } or null.
 */
function extractLatLng(info) {
  if (!info) return null;

  // Path 1: address.location (only if it's a non-empty object, not an array)
  const loc = info.address?.location;
  if (loc && !Array.isArray(loc) && typeof loc === "object") {
    if (loc.latitude !== null && loc.latitude !== undefined && loc.longitude !== null && loc.longitude !== undefined) {
      return { lat: parseFloat(loc.latitude), lng: parseFloat(loc.longitude) };
    }
    if (
      loc.lat !== null &&
      loc.lat !== undefined &&
      ((loc.lng !== null && loc.lng !== undefined) || (loc.lon !== null && loc.lon !== undefined))
    ) {
      return { lat: parseFloat(loc.lat), lng: parseFloat(loc.lng ?? loc.lon) };
    }
  }

  // Path 2: address.geolocation
  const geo = info.address?.geolocation;
  if (geo && typeof geo === "object" && !Array.isArray(geo)) {
    if (geo.latitude !== null && geo.latitude !== undefined && geo.longitude !== null && geo.longitude !== undefined) {
      return { lat: parseFloat(geo.latitude), lng: parseFloat(geo.longitude) };
    }
    if (
      geo.lat !== null &&
      geo.lat !== undefined &&
      ((geo.lng !== null && geo.lng !== undefined) || (geo.lon !== null && geo.lon !== undefined))
    ) {
      return { lat: parseFloat(geo.lat), lng: parseFloat(geo.lng ?? geo.lon) };
    }
  }

  // Path 3: top-level latitude / longitude
  if (
    info.latitude !== null &&
    info.latitude !== undefined &&
    info.longitude !== null &&
    info.longitude !== undefined
  ) {
    return { lat: parseFloat(info.latitude), lng: parseFloat(info.longitude) };
  }

  return null;
}

/**
 * Fallback: resolve approximate lat/lng from country code using centroids.
 * Adds a small random jitter so multiple sites in the same country don't stack.
 */
function centroidFallback(countryCode) {
  if (!countryCode) return null;
  const code = countryCode.trim().toUpperCase().slice(0, 2);
  const c = COUNTRY_CENTROIDS[code];
  if (!c) return null;
  // Jitter ±1° so markers don't overlap perfectly
  const jitter = () => (Math.random() - 0.5) * 2;
  return { lat: c[0] + jitter(), lng: c[1] + jitter() };
}

/**
 * Build a readable address string from the API address fields.
 */
function buildAddress(info) {
  if (!info) return "";
  const addr = info.address || {};
  const parts = [
    addr.address1 || addr.street || addr.street1,
    addr.city,
    addr.state || addr.province,
    addr.country,
    addr.zip || addr.postalCode || addr.zipCode,
  ].filter(Boolean);
  return parts.join(", ");
}

/**
 * Groups chargers by siteId and returns stable site entries.
 */
function buildSiteEntries(fleetData) {
  if (!Array.isArray(fleetData) || fleetData.length === 0) return [];
  const siteMap = {};
  fleetData.forEach(charger => {
    const sid = charger.siteId;
    if (!sid) return;
    if (!siteMap[sid]) {
      siteMap[sid] = { siteId: sid, chargers: [] };
    }
    siteMap[sid].chargers.push(charger);
  });
  return Object.values(siteMap);
}

/**
 * Fetches site info for each unique siteId by calling
 * GET /service/systems/:siteId/systeminfo
 *
 * Only fires when `enabled` is true (i.e. the section is expanded).
 * Groups chargers by siteId so we make one call per site, not per charger.
 *
 * Always returns all sites that have address data (even without exact coords)
 * using country centroids as a fallback for map markers.
 *
 * Returns { locations, isLoading, error }
 */
export function useChargerLocations(fleetData, enabled) {
  const queryClient = useQueryClient();

  // Stable hash for the query key — based on unique siteIds
  const siteIdHash = useMemo(() => {
    if (!Array.isArray(fleetData) || fleetData.length === 0) return "";
    const ids = [...new Set(fleetData.map(c => c.siteId).filter(Boolean))].sort();
    return ids.join(",");
  }, [fleetData]);

  const siteEntries = useMemo(() => buildSiteEntries(fleetData), [fleetData]);

  const {
    data: locations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.sites.locations(siteIdHash),
    queryFn: async () => {
      // Each per-site systeminfo call is cached under its own query key
      // so that StationDashboard SiteCard / ManageStation can reuse results.
      const fetchSite = async entry => {
        try {
          const res = await queryClient.fetchQuery({
            queryKey: queryKeys.sites.systemInfo(entry.siteId),
            queryFn: () => Api.get(`${SYSTEM_INFO_URL}/${entry.siteId}/systeminfo`),
            staleTime: 10 * 60 * 1000,
          });
          return { ...entry, systemInfo: res };
        } catch {
          return { ...entry, systemInfo: null };
        }
      };

      const results = await Promise.all(siteEntries.map(fetchSite));

      return results
        .map(item => {
          const info = item.systemInfo;
          if (!info) return null;

          const addr = info.address || {};
          const country = addr.country || info.tariff?.country || "";

          let coords = extractLatLng(info);
          let isApproximate = false;
          if (!coords) {
            coords = centroidFallback(country);
            isApproximate = true;
          }

          return {
            siteId: item.siteId,
            lat: coords?.lat ?? null,
            lng: coords?.lng ?? null,
            isApproximate,
            chargerCount: item.chargers.length,
            chargers: item.chargers,
            siteName: addr.address1 || addr.city || String(item.siteId),
            address: buildAddress(info),
            country,
            state: addr.state || addr.province || "",
            city: addr.city || "",
            timezone: addr.timezone || "",
          };
        })
        .filter(Boolean);
    },
    enabled: enabled && !isEnphaseUser() && siteEntries.length > 0,
    staleTime: 10 * 60 * 1000, // Location data rarely changes — 10 min
  });

  return { locations, isLoading, error };
}

export default useChargerLocations;
